class ArsCartDrawerUI {
  constructor() {
    this.activePanel = null;
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        requestAnimationFrame(() => this.syncRecoCarousels());
      });
    }

    document.addEventListener('ars:cart-drawer:rendered', () => {
      requestAnimationFrame(() => {
        this.syncRecoCarousels();
        this.clearCartErrors();
        this.refreshQuantityStates();
        this.restoreUtilityState();
        this.initScrollInnerSync();
        this.forceUtilityLabels();
      });
    });

    this.syncRecoCarousels();
    this.restoreUtilityState();
    this.initScrollInnerSync();
    this.forceUtilityLabels();
  }

  forceUtilityLabels() {
    const labels = { shipping: 'Frete', discount: 'Cupom', note: 'Notas' };
    Object.entries(labels).forEach(([key, text]) => {
      const span = document.querySelector(`[data-ars-utility-toggle="${key}"] span`);
      if (span && span.textContent.trim() !== text) {
        span.textContent = text;
      }
    });
  }

  scrollIntoScrollArea(el) {
    if (!el) return;
    const scrollContainer = document.getElementById('CartDrawer-CartItems');
    if (!scrollContainer) return;
    requestAnimationFrame(() => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const overflowBottom = elRect.bottom - containerRect.bottom;
      if (overflowBottom > 0) {
        scrollContainer.scrollBy({ top: overflowBottom + 16, behavior: 'smooth' });
      } else if (elRect.top < containerRect.top) {
        scrollContainer.scrollBy({ top: elRect.top - containerRect.top - 16, behavior: 'smooth' });
      }
    });
  }

  initScrollInnerSync() {
    const outer = document.getElementById('CartDrawer-CartItems');
    const inner = outer ? outer.querySelector('.ars-cart-drawer__scroll-inner') : null;
    if (!outer || !inner) return;

    let spacer = inner.querySelector(':scope > .ars-cart-drawer__scroll-spacer');
    if (!spacer) {
      spacer = document.createElement('div');
      spacer.className = 'ars-cart-drawer__scroll-spacer';
      spacer.setAttribute('aria-hidden', 'true');
      inner.insertBefore(spacer, inner.firstChild);
    }

    const sync = () => {
      spacer.style.height = '0px';
      const contentHeight = inner.scrollHeight;
      const outerHeight = outer.clientHeight;
      const needed = Math.max(0, outerHeight - contentHeight);
      spacer.style.height = `${needed}px`;
    };

    this.syncScrollInner = sync;
    sync();
    requestAnimationFrame(sync);

    if (this.scrollInnerResizeObserver) {
      this.scrollInnerResizeObserver.disconnect();
    }
    this.scrollInnerResizeObserver = new ResizeObserver(() => sync());
    this.scrollInnerResizeObserver.observe(outer);
    this.scrollInnerResizeObserver.observe(inner);

    if (!this._onWindowResizeBound) {
      this._onWindowResizeBound = () => sync();
      window.addEventListener('resize', this._onWindowResizeBound);
    }

    if (window.visualViewport && !this._onVisualViewportResizeBound) {
      this._onVisualViewportResizeBound = () => sync();
      window.visualViewport.addEventListener('resize', this._onVisualViewportResizeBound);
    }
  }

  onDocumentClick(event) {
    const addButton = event.target.closest('[data-reco-add]');
    if (addButton) {
      event.preventDefault();
      this.addRecommendation(addButton);
      return;
    }

    const prevButton = event.target.closest('[data-reco-prev]');
    const nextButton = event.target.closest('[data-reco-next]');
    if (prevButton || nextButton) {
      event.preventDefault();
      const root = (prevButton || nextButton).closest('[data-ars-cart-reco]');
      if (!root) return;
      const cards = [...root.querySelectorAll('[data-reco-card]')];
      if (!cards.length) return;

      let index = Number(root.dataset.recoIndex || 0);
      if (prevButton) index = Math.max(0, index - 1);
      if (nextButton) index = Math.min(cards.length - 1, index + 1);

      this.updateRecoCarousel(root, index);
      return;
    }

    const utilityToggle = event.target.closest('[data-ars-utility-toggle]');
    if (utilityToggle) {
      event.preventDefault();
      this.toggleUtilityPanel(utilityToggle.dataset.arsUtilityToggle, utilityToggle);
      return;
    }

    const shippingCalc = event.target.closest('[data-ars-shipping-calc]');
    if (shippingCalc) {
      event.preventDefault();
      this.calculateShipping();
      return;
    }

    const discountApply = event.target.closest('[data-ars-discount-apply]');
    if (discountApply) {
      event.preventDefault();
      this.applyDiscountCode();
      return;
    }

    const noteSave = event.target.closest('[data-ars-note-save]');
    if (noteSave) {
      event.preventDefault();
      this.saveNote();
    }
  }

  onDocumentKeydown(event) {
    if (event.key !== 'Enter') return;

    const cepInput = event.target.closest('[data-ars-shipping-cep]');
    if (cepInput) {
      event.preventDefault();
      this.calculateShipping();
      return;
    }

    const discountInput = event.target.closest('[data-ars-discount-input]');
    if (discountInput) {
      event.preventDefault();
      this.applyDiscountCode();
    }
  }

  getCartUrl() {
    return window.routes?.cart_url || '/cart';
  }

  getBrazilProvinceName(uf) {
    const provinces = {
      AC: 'Acre',
      AL: 'Alagoas',
      AP: 'Amapá',
      AM: 'Amazonas',
      BA: 'Bahia',
      CE: 'Ceará',
      DF: 'Federal District',
      ES: 'Espírito Santo',
      GO: 'Goiás',
      MA: 'Maranhão',
      MT: 'Mato Grosso',
      MS: 'Mato Grosso do Sul',
      MG: 'Minas Gerais',
      PA: 'Pará',
      PB: 'Paraíba',
      PR: 'Paraná',
      PE: 'Pernambuco',
      PI: 'Piauí',
      RJ: 'Rio de Janeiro',
      RN: 'Rio Grande do Norte',
      RS: 'Rio Grande do Sul',
      RO: 'Rondônia',
      RR: 'Roraima',
      SC: 'Santa Catarina',
      SP: 'São Paulo',
      SE: 'Sergipe',
      TO: 'Tocantins',
    };

    return provinces[uf] || uf;
  }

  async resolveCepAddress(cep) {
    const address = {
      zip: cep,
      country: 'Brazil',
    };

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (!data.erro && data.uf) {
        address.province = this.getBrazilProvinceName(data.uf);
        address.city = data.localidade;
      }
    } catch (error) {
      console.error(error);
    }

    return address;
  }

  buildShippingParams(address) {
    const params = new URLSearchParams();
    params.set('shipping_address[zip]', address.zip);
    params.set('shipping_address[country]', address.country);
    if (address.province) params.set('shipping_address[province]', address.province);
    if (address.city) params.set('shipping_address[city]', address.city);
    return params;
  }

  async wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  async fetchShopifyShippingRates(address) {
    const params = this.buildShippingParams(address);
    const cartUrl = this.getCartUrl();
    const requestInit = {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    };

    await fetch(`${cartUrl}/prepare_shipping_rates.json?${params.toString()}`, {
      ...requestInit,
      method: 'POST',
    });

    for (let attempt = 0; attempt < 8; attempt += 1) {
      await this.wait(attempt === 0 ? 300 : 500);
      const response = await fetch(`${cartUrl}/async_shipping_rates.json?${params.toString()}`, requestInit);
      const data = await response.json();

      if (!response.ok) {
        throw new Error('Erro ao consultar fretes da Shopify.');
      }

      if (Array.isArray(data?.shipping_rates) && data.shipping_rates.length) {
        return data.shipping_rates;
      }
    }

    const fallbackResponse = await fetch(`${cartUrl}/shipping_rates.json?${params.toString()}`, requestInit);
    const fallbackData = await fallbackResponse.json();

    if (!fallbackResponse.ok) {
      throw new Error('Erro ao consultar fretes da Shopify.');
    }

    return fallbackData?.shipping_rates || [];
  }

  formatDeliveryEstimate(rate) {
    if (rate.delivery_range?.min != null && rate.delivery_range?.max != null) {
      const min = rate.delivery_range.min;
      const max = rate.delivery_range.max;
      if (min === max) return `${min} dia(s) úteis`;
      return `${min} a ${max} dias úteis`;
    }

    if (rate.delivery_date) {
      const date = new Date(rate.delivery_date);
      if (!Number.isNaN(date.getTime())) {
        return `Entrega até ${date.toLocaleDateString('pt-BR')}`;
      }
    }

    return 'Prazo confirmado no checkout';
  }

  positionPanelCaret(panel, toggle) {
    if (!panel || !toggle) return;
    const row = toggle.closest('.ars-cart-drawer__utilities-buttons');
    if (!row) return;

    const rowRect = row.getBoundingClientRect();
    const btnRect = toggle.getBoundingClientRect();
    const centerPct = ((btnRect.left + btnRect.width / 2 - rowRect.left) / rowRect.width) * 100;
    const clamped = Math.min(92, Math.max(8, centerPct));
    panel.style.setProperty('--caret-left', `${clamped}%`);
  }

  toggleUtilityPanel(panelName, toggleEl) {
    const panel = document.querySelector(`[data-ars-panel="${panelName}"]`);
    const toggle = toggleEl || document.querySelector(`[data-ars-utility-toggle="${panelName}"]`);
    if (!panel || !toggle) return;

    const isOpen = !panel.hidden;

    document.querySelectorAll('[data-ars-panel]').forEach((node) => {
      node.hidden = true;
    });
    document.querySelectorAll('[data-ars-utility-toggle]').forEach((node) => {
      node.setAttribute('aria-expanded', 'false');
      node.classList.remove('is-active');
    });

    if (!isOpen) {
      this.positionPanelCaret(panel, toggle);
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      toggle.classList.add('is-active');
      this.activePanel = panelName;
      sessionStorage.setItem('ars-cart-active-panel', panelName);

      const focusTarget = panel.querySelector('input, textarea');
      if (focusTarget) focusTarget.focus();
      this.syncScrollInner?.();
      this.scrollIntoScrollArea(panel);
      return;
    }

    this.activePanel = null;
    sessionStorage.removeItem('ars-cart-active-panel');
    this.syncScrollInner?.();
  }

  restoreUtilityState() {
    const cepInput = document.querySelector('[data-ars-shipping-cep]');
    const savedCep = sessionStorage.getItem('ars-cart-shipping-cep');
    if (cepInput && savedCep) {
      cepInput.value = savedCep;
    }

    const activePanel = sessionStorage.getItem('ars-cart-active-panel');
    if (activePanel) {
      const panel = document.querySelector(`[data-ars-panel="${activePanel}"]`);
      const toggle = document.querySelector(`[data-ars-utility-toggle="${activePanel}"]`);
      if (panel && toggle) {
        this.positionPanelCaret(panel, toggle);
        panel.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        toggle.classList.add('is-active');
        this.activePanel = activePanel;
      }
    }

    this.bindCepMask();
  }

  bindCepMask() {
    const cepInput = document.querySelector('[data-ars-shipping-cep]');
    if (!cepInput || cepInput.dataset.arsCepBound === 'true') return;

    cepInput.dataset.arsCepBound = 'true';
    cepInput.addEventListener('input', () => {
      let value = cepInput.value.replace(/\D/g, '');
      if (value.length > 5) {
        value = `${value.slice(0, 5)}-${value.slice(5, 8)}`;
      }
      cepInput.value = value;
      sessionStorage.setItem('ars-cart-shipping-cep', value);
    });
  }

  formatCurrency(value) {
    const amount = Number(value);
    if (Number.isNaN(amount)) return value;

    return amount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }

  setShippingResults(html, className = '') {
    const results = document.querySelector('[data-ars-shipping-results]');
    if (!results) return;
    results.className = `ars-cart-drawer__panel-results${className ? ` ${className}` : ''}`;
    results.innerHTML = html;
    if (html) this.scrollIntoScrollArea(results);
  }

  async calculateShipping() {
    const cepInput = document.querySelector('[data-ars-shipping-cep]');
    const button = document.querySelector('[data-ars-shipping-calc]');
    if (!cepInput) return;

    const recipientCep = cepInput.value.replace(/\D/g, '');
    if (recipientCep.length !== 8) {
      this.setShippingResults('<p class="ars-cart-drawer__panel-feedback is-error">CEP inválido. Digite 8 números.</p>');
      return;
    }

    if (button) button.disabled = true;
    this.setShippingResults('<p class="ars-cart-drawer__panel-feedback is-loading">Calculando frete...</p>');

    try {
      const cart = await fetch(`${this.getCartUrl()}.js`).then((response) => response.json());
      if (!cart.item_count) {
        this.setShippingResults('<p class="ars-cart-drawer__panel-feedback is-error">Adicione itens ao carrinho para calcular o frete.</p>');
        return;
      }

      const address = await this.resolveCepAddress(recipientCep);
      const rates = await this.fetchShopifyShippingRates(address);

      if (!rates.length) {
        this.setShippingResults('<p class="ars-cart-drawer__panel-feedback is-error">Nenhuma opção de frete encontrada para este CEP.</p>');
        return;
      }

      const sortedRates = [...rates].sort((a, b) => Number(a.price) - Number(b.price));
      const rows = sortedRates
        .slice(0, 5)
        .map((rate) => {
          const name = rate.presentment_name || rate.name || 'Frete';
          const price = this.formatCurrency(rate.price);
          const delivery = this.formatDeliveryEstimate(rate);

          return `
            <div class="ars-cart-drawer__shipping-option">
              <div class="ars-cart-drawer__shipping-option-info">
                <strong>${name}</strong>
                <small>${delivery}</small>
              </div>
              <div class="ars-cart-drawer__shipping-option-price">${price}</div>
            </div>
          `;
        })
        .join('');

      this.setShippingResults(rows);
    } catch (error) {
      console.error(error);
      this.setShippingResults(
        '<p class="ars-cart-drawer__panel-feedback is-error">Não foi possível calcular o frete. Verifique o CEP e tente novamente.</p>'
      );
    } finally {
      if (button) button.disabled = false;
    }
  }

  setDiscountMessage(message, type = '') {
    const messageEl = document.querySelector('[data-ars-discount-message]');
    if (!messageEl) return;
    messageEl.textContent = message;
    messageEl.className = `ars-cart-drawer__panel-message${type ? ` is-${type}` : ''}`;
    if (message) this.scrollIntoScrollArea(messageEl);
  }

  applyDiscountViaShopify(code) {
    return new Promise((resolve) => {
      const root = window.Shopify?.routes?.root || '/';
      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none';
      iframe.src = `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}cart`)}`;

      const cleanup = () => {
        iframe.remove();
        resolve();
      };

      iframe.onload = () => window.setTimeout(cleanup, 400);
      iframe.onerror = cleanup;
      document.body.appendChild(iframe);
      window.setTimeout(cleanup, 3000);
    });
  }

  async refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const sectionIds = cartDrawer.getSectionsToRender().map((section) => section.id).join(',');
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}?sections=${sectionIds}`);
    const sections = await response.json();

    cartDrawer.renderContents({ sections });
  }

  async applyDiscountCode() {
    const input = document.querySelector('[data-ars-discount-input]');
    const button = document.querySelector('[data-ars-discount-apply]');
    if (!input) return;

    const code = input.value.trim();
    if (!code) {
      this.setDiscountMessage('Digite um código de desconto.', 'error');
      return;
    }

    if (button) button.disabled = true;
    this.setDiscountMessage('Aplicando cupom...', 'loading');

    try {
      const beforeCart = await fetch(`${window.routes?.cart_url || '/cart'}.js`).then((response) => response.json());
      const beforeTotal = beforeCart.total_price;
      const beforeDiscounts = beforeCart.cart_level_discount_applications || [];

      await this.applyDiscountViaShopify(code);

      const afterCart = await fetch(`${window.routes?.cart_url || '/cart'}.js`).then((response) => response.json());
      const afterDiscounts = afterCart.cart_level_discount_applications || [];
      const normalizedCode = code.toUpperCase();

      const discountApplied =
        afterCart.total_price < beforeTotal ||
        afterDiscounts.length > beforeDiscounts.length ||
        afterDiscounts.some((discount) => (discount.title || '').toUpperCase() === normalizedCode);

      if (!discountApplied) {
        this.setDiscountMessage('Código inválido, expirado ou não aplicável ao carrinho.', 'error');
        return;
      }

      await this.refreshCartDrawer();
      input.value = '';
      this.setDiscountMessage('Cupom aplicado com sucesso!', 'success');
      window.setTimeout(() => this.setDiscountMessage(''), 3500);

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ars-cart-drawer', cartData: afterCart });
      }
    } catch (error) {
      console.error(error);
      this.setDiscountMessage('Erro ao aplicar o cupom. Tente novamente.', 'error');
    } finally {
      if (button) button.disabled = false;
    }
  }

  setNoteMessage(message, type = '') {
    const messageEl = document.querySelector('[data-ars-note-message]');
    if (!messageEl) return;
    messageEl.textContent = message;
    messageEl.className = `ars-cart-drawer__panel-message${type ? ` is-${type}` : ''}`;
    if (message) this.scrollIntoScrollArea(messageEl);
  }

  async saveNote() {
    const textarea = document.querySelector('[data-ars-panel="note"] textarea');
    const button = document.querySelector('[data-ars-note-save]');
    if (!textarea) return;

    if (button) button.disabled = true;
    this.setNoteMessage('Salvando...', 'loading');

    try {
      const response = await fetch(`${this.getCartUrl()}/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ note: textarea.value }),
      });

      if (!response.ok) throw new Error('Falha ao salvar nota');

      this.setNoteMessage('Nota salva! Sua observação foi adicionada ao pedido.', 'success');
      window.setTimeout(() => this.setNoteMessage(''), 3500);
    } catch (error) {
      console.error(error);
      this.setNoteMessage('Não foi possível salvar a nota. Tente novamente.', 'error');
    } finally {
      if (button) button.disabled = false;
    }
  }

  syncRecoCarousels() {
    document.querySelectorAll('[data-ars-cart-reco]').forEach((root) => {
      const index = Number(root.dataset.recoIndex || 0);
      this.updateRecoCarousel(root, index);
    });
  }

  updateRecoCarousel(root, index) {
    const track = root.querySelector('[data-reco-track]');
    const cards = [...root.querySelectorAll('[data-reco-card]')];
    const countEl = root.querySelector('[data-reco-count]');
    const prevBtn = root.querySelector('[data-reco-prev]');
    const nextBtn = root.querySelector('[data-reco-next]');
    if (!track || !cards.length) return;

    const safeIndex = Math.max(0, Math.min(index, cards.length - 1));
    root.dataset.recoIndex = String(safeIndex);

    const card = cards[safeIndex];
    track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });

    if (countEl) countEl.textContent = `${safeIndex + 1}/${cards.length}`;
    if (prevBtn) prevBtn.disabled = safeIndex === 0;
    if (nextBtn) nextBtn.disabled = safeIndex >= cards.length - 1;
  }

  async addRecommendation(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    const variantId = Number(button.dataset.recoAdd);
    if (!variantId) return;

    button.setAttribute('aria-disabled', 'true');

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const payload = {
        items: [{ id: variantId, quantity: 1 }],
      };

      if (cartDrawer) {
        payload.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        payload.sections_url = '/cart';
      }

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status) {
        if (cartDrawer && data.sections) {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(data);
        }
        return;
      }

      if (cartDrawer && data.sections) {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(data);
      }

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ars-cart-drawer', cartData: data });
      }
    } catch (error) {
      console.error(error);
    } finally {
      button.removeAttribute('aria-disabled');
    }
  }

  clearCartErrors() {
    const globalErrors = document.getElementById('CartDrawer-CartErrors');
    if (globalErrors) globalErrors.textContent = '';

    document.querySelectorAll('cart-drawer .cart-item__error-text').forEach((errorText) => {
      errorText.innerHTML = '';
    });
  }

  refreshQuantityStates() {
    document.querySelectorAll('cart-drawer quantity-input').forEach((quantityInput) => {
      if (typeof quantityInput.validateQtyRules === 'function') {
        quantityInput.validateQtyRules();
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ArsCartDrawerUI();
});
